import { create } from "zustand";
import { APP_ENV } from "src/lib/appEnv";
import {
  listPortalNotifications,
  toMillis,
} from "src/lib/portalNotificationService";
import type {
  BroadcastEnv,
  PortalNotification,
  PortalNotificationStatus,
} from "src/types/portalNotifications";

const PAGE_SIZE = 20;

/** Monotonic counter so a slow earlier load cannot overwrite a newer one. */
let loadGeneration = 0;

export type PortalNotificationsStore = {
  /** Pinned to this deployment's APP_ENV — not user-switchable. */
  env: BroadcastEnv;
  statusFilter: PortalNotificationStatus | null;
  items: PortalNotification[];
  pageNumber: number;
  totalPages: number;
  totalElements: number;
  loading: boolean;
  error: string | null;

  setStatusFilter: (status: PortalNotificationStatus | null) => void;
  setPage: (page: number) => void;
  load: () => Promise<void>;
  reset: () => void;
};

export const usePortalNotificationsStore = create<PortalNotificationsStore>(
  (set, get) => ({
    env: APP_ENV,
    statusFilter: null,
    items: [],
    pageNumber: 1,
    totalPages: 0,
    totalElements: 0,
    loading: false,
    error: null,

    setStatusFilter: (statusFilter) => {
      set({ statusFilter, pageNumber: 1 });
      void get().load();
    },

    setPage: (pageNumber) => {
      set({ pageNumber });
      void get().load();
    },

    load: async () => {
      const generation = ++loadGeneration;
      const { env, statusFilter, pageNumber } = get();
      set({ loading: true, error: null });
      try {
        const res = await listPortalNotifications(
          env,
          statusFilter,
          pageNumber,
          PAGE_SIZE,
        );
        // Ignore stale responses from an earlier in-flight load.
        if (generation !== loadGeneration) return;
        set({
          items: res.notifications ?? [],
          totalPages: Number(res.meta?.totalPages ?? 0),
          totalElements: Number(res.meta?.totalElements ?? 0),
          loading: false,
        });
      } catch (e) {
        if (generation !== loadGeneration) return;
        set({
          error:
            e instanceof Error ? e.message : "Failed to load notifications",
          loading: false,
        });
      }
    },

    reset: () => {
      loadGeneration += 1;
      set({
        env: APP_ENV,
        statusFilter: null,
        items: [],
        pageNumber: 1,
        totalPages: 0,
        totalElements: 0,
        loading: false,
        error: null,
      });
    },
  }),
);

/** Shared display helpers for list + preview. */
export function formatPortalNotificationStatus(
  n: PortalNotification,
): PortalNotificationStatus {
  if (n.status.endsWith("ARCHIVED")) return "ARCHIVED";
  if (n.status.endsWith("PUBLISHED")) return "PUBLISHED";
  return "DRAFT";
}

/** True when both ends of the publication window are set (non-zero). */
export function hasPublicationWindow(n: PortalNotification): boolean {
  const start = toMillis(n.startAtUnixMillis);
  const end = toMillis(n.endAtUnixMillis);
  return start > 0 && end > 0;
}

/** Local "start → end" label, or "" when the draft has no window yet. */
export function formatWindow(n: PortalNotification): string {
  if (!hasPublicationWindow(n)) return "";
  const start = toMillis(n.startAtUnixMillis);
  const end = toMillis(n.endAtUnixMillis);
  const fmt = (ms: number) =>
    new Date(ms).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  return `${fmt(start)} → ${fmt(end)}`;
}
